import React, { useEffect, useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  useApi,
  discoveryApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import {
  Table,
  TableColumn,
  Progress,
  InfoCard,
} from '@backstage/core-components';
import { Typography } from '@material-ui/core';

interface AuditRecord {
  id: string;
  timestamp: string;
  cloud: 'azure';
  entityRef: string;
  action: string;
  actor: string;
  status: 'SUCCEEDED' | 'FAILED' | 'PENDING' | 'UNKNOWN';
  message: string;
  runUrl?: string;
}

const columns: TableColumn<AuditRecord>[] = [
  {
    title: 'Timestamp',
    field: 'timestamp',
    width: '180px',
    render: row => new Date(row.timestamp).toLocaleString(),
  },
  {
    title: 'Action',
    field: 'action',
    width: '160px',
  },
  {
    title: 'Actor',
    field: 'actor',
    width: '180px',
  },
  {
    title: 'Status',
    field: 'status',
    width: '100px',
    render: row => (
      <span
        style={{
          color:
            row.status === 'SUCCEEDED'
              ? '#1db954'
              : row.status === 'FAILED'
              ? '#e53935'
              : undefined,
          fontWeight: 600,
        }}
      >
        {row.status}
      </span>
    ),
  },
  {
    title: 'Message',
    field: 'message',
    render: row =>
      row.runUrl ? (
        <a href={row.runUrl} target="_blank" rel="noopener noreferrer">
          {row.message}
        </a>
      ) : (
        <>{row.message}</>
      ),
  },
];

export function AzureAuditCard() {
  const { entity } = useEntity();
  const entityRef = `${entity.kind.toLowerCase()}:${
    entity.metadata.namespace ?? 'default'
  }/${entity.metadata.name}`;
  const discoveryApi = useApi(discoveryApiRef);
  const identityApi = useApi(identityApiRef);

  const storageAccount = (entity.metadata as any).auditStorageAccount as
    | string
    | undefined;
  const tableName = (entity.metadata as any).auditTable as string | undefined;

  const [records, setRecords] = useState<AuditRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!storageAccount || !tableName) return;

    let cancelled = false;
    setLoading(true);
    setError(undefined);

    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('azure-apps');
        const { token } = await identityApi.getCredentials();
        const res = await fetch(
          `${baseUrl}/audit?entityRef=${encodeURIComponent(
            entityRef,
          )}&storageAccount=${encodeURIComponent(
            storageAccount,
          )}&tableName=${encodeURIComponent(tableName)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!res.ok) {
          const body = await res.text();
          throw new Error(`${res.status}: ${body}`);
        }
        const data = await res.json();
        if (!cancelled) setRecords(data);
      } catch (e: any) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [discoveryApi, entityRef, identityApi, storageAccount, tableName]);

  if (!storageAccount || !tableName) {
    return (
      <InfoCard title="Operations">
        <Typography variant="body2" color="textSecondary">
          No audit storage configured. Run the provisioning pipeline to enable
          operation history.
        </Typography>
      </InfoCard>
    );
  }

  if (loading) {
    return (
      <InfoCard title="Operations">
        <Progress />
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard title="Operations">
        <Typography variant="body2" color="error">
          Failed to load audit records: {error}
        </Typography>
      </InfoCard>
    );
  }

  return (
    <Table
      title="Operations"
      options={{ paging: true, pageSize: 10, search: false }}
      columns={columns}
      data={records}
    />
  );
}
